import React, { useEffect, useState } from 'react';
import {
  CheckCircle2,
  LogOut,
  LayoutDashboard,
  Eye,
  EyeOff
} from 'lucide-react';

import Producao from './Producao';
import Faturamento from './Faturamento';
import Expedicao from './Expedicao';
import Conta from './Conta';
import Estoque from './Estoque';
import Login from './Login';
import ContaPagar from './ContaPagar';

export default function App() {
  const [valorRealizado, setValorRealizado] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLogged, setIsLogged] = useState(false);
  const base_url = 'https://onboard.fck.ind.br';
  const [valorDevolucoes, setValorDevolucoes] = useState(0);
  const [mostraProducao, setMostraProducao] = useState(0);
  const [mostraRecebiveis, setMostraRecebiveis] = useState(0);
  const [mostraFaturamento, setMostraFaturamento] = useState(0);
  const [mostraExpedicao, setMostraExpedicao] = useState(0);
  const [usuario, setUsuario] = useState("");
  const [showEstoque, setShowEstoque] = useState(true);
  
  // Sinal para forçar a atualização dos componentes filhos
  const [refreshSignal, setRefreshSignal] = useState(0);

  useEffect(() => {
    document.title = "Onboard FCK";
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('@user_token');
    const permissaoProducao = localStorage.getItem('@mostra_producao');
    const permissaoRecebiveis = localStorage.getItem('@mostraRecebiveis');
    const permissaoFaturamento = localStorage.getItem('@mostraFaturamento');
    const permissaoExpedicao = localStorage.getItem('@mostraExpedicao');
    const nomeUsuario = localStorage.getItem('@NomeUsuario');
     
    setMostraRecebiveis(Number(permissaoRecebiveis));
    setMostraFaturamento(Number(permissaoFaturamento));
    setMostraExpedicao(Number(permissaoExpedicao));
    setMostraProducao(Number(permissaoProducao));
    setUsuario(nomeUsuario || "");

    if (token) {
      setIsLogged(true);
      buscarDadosGerais(token);
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let interval;
    if (isLogged) {
      const cincoMinutos = 5 * 60 * 1000;

      interval = setInterval(() => {
        const token = localStorage.getItem('@user_token');        
        if (token) {
          console.log("Atualizando painel automaticamente...");
          buscarDadosGerais(token);
          // Dispara o sinal para os filhos (Faturamento, etc) buscarem novos dados
          setRefreshSignal(prev => prev + 1);
        }
      }, cincoMinutos);
    }

    return () => clearInterval(interval);
  }, [isLogged]);

  const buscarDadosGerais = async (token) => {
    try {
      const response = await fetch(`${base_url}/api/dashboard/geral`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }

      const json = await response.json();
      setData(Array.isArray(json) ? json[0] : json);
    } catch (err) {
      console.error("Erro ao buscar dados gerais:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('@user_token');
    setIsLogged(false);
    window.location.reload();
  };

  if (loading) return (
    <div className="h-screen bg-[#0d1117] flex flex-col items-center justify-center text-green-500 font-mono italic animate-pulse">
      <LayoutDashboard size={48} className="mb-4" />
      <span className="tracking-[0.3em]">AUTENTICANDO SISTEMA...</span>
    </div>
  );

  if (!isLogged) {
    return <Login onLoginSuccess={() => {
      setIsLogged(true);
      setLoading(true);
      const novoToken = localStorage.getItem('@user_token');
      buscarDadosGerais(novoToken);
    }} />;
  }

  const metaMes = data?.metaMes || 0;
  const percentualMeta = metaMes > 0 ? ((valorRealizado / metaMes) * 100).toFixed(0) : 0;

  return (
    <div className="min-h-screen bg-[#0d1117] text-gray-400 p-4 lg:p-8 font-sans">
      <div className="max-w-[1400px] mx-auto space-y-4">

        <div className="flex justify-between items-center border-b border-gray-800 pb-2">
          <div>
            <h1 className="text-white tracking-[0.8em] uppercase text-[10px] font-black opacity-50">
              COMPUTADOR DE BORDO
            </h1>
            <h1 className="text-white tracking-[0.8em] uppercase text-[5px] font-black opacity-45">
              Login: {usuario}
            </h1>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-[9px] text-red-500 hover:text-red-400 font-bold uppercase transition-colors"
          >
            <LogOut size={14} /> Sair do Sistema
          </button>
        </div>

        <div className="bg-[#161b22] p-4 rounded border border-gray-800 shadow-2xl">
          <div className="flex justify-between items-center mb-2">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-green-500" />
                <span className="text-[10px] font-bold uppercase">Meta do Mês:
                  <span className="text-green-500 ml-2 font-mono italic">
                    R$ {metaMes.toLocaleString('pt-BR')}
                  </span>
                </span>
              </div>
            </div>
            <span className="text-green-500 font-bold font-mono">{percentualMeta}%</span>
          </div>

          <div className="mt-4 mb-4">
            <div className="w-full bg-gray-900 h-2 rounded-full overflow-hidden border border-gray-800">
              <div
                className="bg-gradient-to-r from-orange-600 to-green-500 h-full transition-all duration-1000"
                style={{ width: `${Math.min(percentualMeta, 100)}%` }}
              />
            </div>
          </div>

          <div className="flex items-center w-full">
            <button
              onClick={() => setShowEstoque(!showEstoque)}
              className="ml-auto flex items-center gap-2 px-2 py-1 bg-gray-900 border border-gray-700 rounded hover:bg-gray-800 transition-all"
            >
              {showEstoque ? (
                <EyeOff size={14} className="text-red-500" />
              ) : (
                <Eye size={14} className="text-blue-500" />
              )}
              <span className="text-[9px] font-black uppercase tracking-tighter">
                {showEstoque ? "Ocultar Estoque" : "Mostrar Estoque"}
              </span>
            </button>
          </div>
        </div>
         
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {mostraProducao > 0 && (<Producao refreshSignal={refreshSignal} />)}
          {mostraExpedicao > 0 && (<Expedicao refreshSignal={refreshSignal} />)}         
          {mostraFaturamento > 0 && (            
            <Faturamento
              onDataLoad={setValorDevolucoes}
              onRealizadoLoad={setValorRealizado}             
              valorMeta={metaMes}
               refreshSignal={refreshSignal} 
            />
          )}
        </div>
         
        {showEstoque && (
          <Estoque
            pedidosCancelados={data?.pedidosCancelados || 0}
            valordevolucoesMes={valorDevolucoes || 0}
            mostraFaturamento={mostraFaturamento || 0}
            mostraExpedicao={mostraExpedicao || 0}
          />
        )}

        {mostraRecebiveis > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Conta />
            <ContaPagar />
          </div>
        )}

        <footer className="text-center pt-8 opacity-50">
          <p className="text-[8px] uppercase tracking-[0.5em]">FCK Indústria - Sistema de Gestão Realtime</p>
        </footer>
      </div>
    </div>
    
  );

}